import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import { parseISO } from 'date-fns'
import './index.css'

const PRIMARY = am5.color(0x5a7aa6)

const DashboardLogsActionsByDay = ({ chartID, dataSet }) => {
  useLayoutEffect(() => {
    const chartData = (dataSet.byDay || []).map((item) => ({
      date: parseISO(item.date).getTime(),
      count: item.count
    }))

    const root = am5.Root.new(chartID)
    root.setThemes([am5themes_Animated.new(root)])
    root._logo.dispose()

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        paddingLeft: 0,
        paddingRight: 10,
        paddingTop: 10,
        paddingBottom: 0
      })
    )

    // Cursor — vertical line only
    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }))
    cursor.lineY.set('visible', false)

    // X axis — date
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: 'day', count: 1 },
        gridIntervals: [{ timeUnit: 'day', count: 1 }],
        tooltip: am5.Tooltip.new(root, {}),
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 50 })
      })
    )
    xAxis.get('dateFormats')['day'] = 'dd MMM'
    xAxis.get('renderer').labels.template.setAll({ fontSize: 10 })
    xAxis.get('renderer').grid.template.set('strokeOpacity', 0.06)

    // Y axis
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        maxPrecision: 0,
        extraMax: 0.15,
        strictMinMax: true,
        renderer: am5xy.AxisRendererY.new(root, {})
      })
    )
    yAxis.get('renderer').labels.template.setAll({ fontSize: 10 })
    yAxis.get('renderer').grid.template.set('strokeOpacity', 0.06)

    // Smoothed area series with gradient fill
    const series = chart.series.push(
      am5xy.SmoothedXLineSeries.new(root, {
        name: 'Actions',
        xAxis,
        yAxis,
        valueYField: 'count',
        valueXField: 'date',
        tooltip: am5.Tooltip.new(root, {
          labelText: '[bold]{valueY}[/] actions\n{valueX.formatDate("dd MMM yyyy")}'
        })
      })
    )

    // Gradient fill under curve
    series.fills.template.setAll({
      fillOpacity: 1,
      visible: true,
      fillGradient: am5.LinearGradient.new(root, {
        stops: [
          { color: PRIMARY, opacity: 0.35 },
          { color: PRIMARY, opacity: 0.0 }
        ],
        rotation: 90
      })
    })

    series.strokes.template.setAll({
      stroke: PRIMARY,
      strokeWidth: 2.5
    })

    // Bullet dots
    series.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 4,
          fill: PRIMARY,
          stroke: am5.color(0xffffff),
          strokeWidth: 2
        })
      })
    )

    // Value labels above dots
    series.bullets.push(() =>
      am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: '{valueY}',
          centerX: am5.p50,
          centerY: am5.p100,
          dy: -6,
          fontSize: 10,
          fontWeight: 'bold',
          populateText: true
        })
      })
    )

    series.data.setAll(chartData)
    series.appear(1000)
    chart.appear(1000, 100)

    const container = document.getElementById(chartID)
    const ro = new ResizeObserver(() => root.resize())
    if (container) ro.observe(container)

    return () => {
      ro.disconnect()
      root.dispose()
    }
  }, [chartID, dataSet])

  return <div id={chartID} className='logs-actions-day-chart' />
}

DashboardLogsActionsByDay.propTypes = {
  chartID: PropTypes.string.isRequired,
  dataSet: PropTypes.object.isRequired
}

export default DashboardLogsActionsByDay
